import React, {useEffect, useRef} from 'react'

export interface UseClickOut<T extends HTMLElement> {
  ref: React.RefObject<T> | React.MutableRefObject<T | undefined>
  cb: (e: MouseEvent) => void
  capture?: boolean
  type?: 'mousedown' | 'click'
}

export const useClickOut = <T extends HTMLElement>({
  ref,
  cb,
  capture,
  type = 'mousedown'
}: UseClickOut<T>): void => {
  // Prevents listeners being reapplied on each render when a callback is an anonymous function
  const cbRef = useRef(cb)
  useEffect(() => {
    cbRef.current = cb
  }, [cb])

  useEffect(() => {
    const listener = (e: MouseEvent) => {
      // Check if a Tippy tooltip is present; it should be closed first
      let tooltip = !!document.querySelector('[data-tippy-root]')
      // Check if there are any MoveToBody that doesn't contain referenced element; they should be closed first too
      document.querySelectorAll('[data-origin] > :not(.hidden)').forEach(el => {
        if (ref.current && !el.contains(ref.current)) {
          tooltip ||= true
        }
      })

      if (!ref?.current?.contains(e.target as Node) && !tooltip) {
        cbRef.current?.(e)
      }
    }

    document.addEventListener(type, listener, !!capture)
    return () => document.removeEventListener(type, listener, !!capture)
  }, [cbRef, ref, type, capture])
}

export interface ClickOutProps {
  cb: () => void
  children: React.ReactNode
  capture?: boolean
  className?: string
}

export const ClickOut = ({cb, children, capture, className}: ClickOutProps) => {
  const ref = useRef<HTMLDivElement>(null)

  useClickOut({ref, cb, capture})

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  )
}
